import * as React from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useContext } from 'react';
import { CmicContext } from '../context/CmicContext';
import { baseFontFamilyStyle } from '../shared/styles';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { ActionContext } from '../context/ActionContext';
import { CMIC_FIELD_REST__NAMES, CMIC_TREE_NODE_TYPES } from '../shared/Constants';
import { createOpenURL, createOpenURLImaging } from '../utility/UtilityFunctions';
import { URL_REGEX, ACTION_ROLE } from '../shared/Constants';
import { MessageBarType } from '@fluentui/react';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { isShowMetaPanelData } from '../pages/metaDataPanel/metaDataPanel';
import { COLORS } from '../utility/tasks/colors';
import { handleLoginUserViaAuth2, isInternetAvailable, checkExtraSlashAPIURL } from '../shared/UtilityFunctions';
import { segmentDefaultTrackCall } from '../utility/segment/segmentUtility';

export default function CustomBreadCrumbs({ isDisabled = { current: false }, parentStyle = {} }) {
	const { currentOfficeTheme, userInfo, setShowToast, selectedProject } = useContext(CmicContext);
	const { docDataForUpload, breadcrumbs = [], activeNode, handleBreadCrumbClicked } = useContext(ActionContext);

	const StyledBreadcrumbs = styled(Breadcrumbs)`
		.MuiBreadcrumbs-li {
			display: block;
			max-width: fit-content;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			width: 100%;
			text-align: start;
			flex: 1;
		}
		,
		.MuiBreadcrumbs-li a div {
			overflow: hidden;
			text-overflow: ellipsis;
		}
	`;

	React.useEffect(() => {}, [activeNode]);

	const openURLOnCellClick = async data => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		segmentTrackCall(data);
		try {
			// Fetching required URL
			const baseURL = checkExtraSlashAPIURL(userInfo.serverURL);
			const result = await axios.get(`${baseURL}${CMIC_FIELD_REST__NAMES.apps_root_uri}`, {
				...handleLoginUserViaAuth2(userInfo),
			});
			// Creating required link as per given requirements
			let URL = '';
			if (activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION) {
				URL = createOpenURLImaging(activeNode, result.data.uri, data);
			} else {
				URL = createOpenURL(activeNode, result.data.uri, selectedProject.PmpVUuid, data);
			}

			// Checking if we are processing a URL
			if (URL_REGEX.test(URL)) {
				window.open(URL);
			} else {
				setShowToast({
					isVisible: true,
					msg: `Unable to redirect at the moment`,
					type: MessageBarType.error,
				});
			}
		} catch (error) {
			console.log('ERROR openURLOnCellClick()', error);
			setShowToast({
				isVisible: true,
				msg: `Unable to redirect at the moment`,
				type: MessageBarType.error,
			});
		}
	};

	const segmentTrackCall = data => {
		// Segment TRACK call triggered when the user clicks the hyperlink icon to launch Construct PM on the web
		if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser) {
			if (data) {
				let segmentObj = {
					contactCode: userInfo?.info?.contactCode,
					timeStamp: new Date(),
					projectName: selectedProject?.PmpProjName,
					projectId: selectedProject?.PmpVUuid,
				};

				if (data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI) {
					segmentObj.RFIId = data?.PmscrfiRfiId;
				} else if (data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE) {
					segmentObj.issueId = data?.DmiIssueId;
				} else if (data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL) {
					segmentObj.journalId = data?.PmjJournalId;
				} else if (data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS) {
					segmentObj.submittalId = data?.PmsmSbmtId;
				}

				let eventName = 'CONSTRUCT_PM_REDIRECTION';
				segmentDefaultTrackCall(eventName, segmentObj);
			}
		}
	};

	const handleClick = (event, data) => {
		event.preventDefault();
		isDisabled.current = true;
		handleBreadCrumbClicked(data);
	};

	const breadCrumbTextUI = (label = '') => {
		if (label === 'Home') return 'Home';
		else if (label.length > 23) return label.substring(0, 22) + '...';
		else return label;
	};

	const breadcrumbUI = breadcrumbs.map(item => (
		<Link
			underline="hover"
			key={item.id}
			style={{
				fontSize: 'xlarge',
				cursor: 'pointer',
				color: isShowMetaPanelData(item.id)
					? COLORS.OUTLOOK_PRIMARY_BUTTON_COLOR
					: currentOfficeTheme?.bodyForegroundColor,
				display: 'flex',
				flexDirection: 'row',
				alignContent: 'center',
			}}
			onClick={e => {
				handleClick(e, item);
				isShowMetaPanelData(item.id) ? openURLOnCellClick(docDataForUpload[activeNode]) : null;
			}}
		>
			<div> {breadCrumbTextUI(item.label)}</div>
			{isShowMetaPanelData(item.id) && (
				<div>
					{' '}
					<MdOutlineOpenInNew style={{ marginTop: '5px', marginLeft: '3px' }} />
				</div>
			)}
		</Link>
	));
	if (
		breadcrumbs[0]?.id === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT ||
		breadcrumbs[0]?.id === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_DOCUMENTS
	) {
		return (
			<Stack width={'90%'} margin="0 0.5rem 0.2rem 0.5rem" style={parentStyle}>
				<StyledBreadcrumbs
					style={{ marginTop: '5px' }}
					separator={
						<NavigateNextIcon
							style={{ color: currentOfficeTheme?.bodyForegroundColor, fontFamily: baseFontFamilyStyle }}
							fontSize="small"
						/>
					}
					aria-label="breadcrumb"
				>
					{breadcrumbUI}
				</StyledBreadcrumbs>
			</Stack>
		);
	}
	return (
		<Stack spacing={2} width={'90%'} margin="0 0.5rem 0.2rem 0.5rem" style={parentStyle}>
			<Breadcrumbs
				style={{ marginTop: '1px' }}
				separator={
					<NavigateNextIcon
						style={{ color: currentOfficeTheme?.bodyForegroundColor, fontFamily: baseFontFamilyStyle }}
						fontSize="small"
					/>
				}
				aria-label="breadcrumb"
			>
				{breadcrumbUI}
			</Breadcrumbs>
		</Stack>
	);
}
